import React, { useState } from 'react';
import { 
  Building2, GraduationCap, Award, CheckCircle2, Calendar, Clock, 
  MapPin, ArrowRight, ShieldCheck, Sparkles, BookOpen, Users, 
  FileText, Download, Printer, ExternalLink, ChevronRight, AlertCircle, 
  Briefcase, Star, MessageSquare, DollarSign, Layers, ArrowLeft,
  Check, Lock, ChevronDown, Compass, UserCheck
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';
import { SocialPillButtons, SocialIconLinks } from './SocialLinks';

// Comprehensive Data for the 6 Core Academic Degrees
export const PROGRAMME_DETAILS = {
  'phd-ba': {
    id: 'phd-ba',
    code: 'PhD BA',
    degree: 'Doctor of Philosophy (PhD)',
    name: 'PhD in Business Administration',
    department: 'Business Administration',
    category: 'Doctoral Research Track',
    badge: 'Terminal Academic Doctorate',
    duration: '3 - 5 Years (6 - 10 Semesters)',
    mode: 'Executive Weekend Seminars & Advanced Dissertation',
    feePerSession: '₦850,000',
    feePerSemester: '₦450,000',
    totalCredits: '60 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'Advance frontier academic scholarship and empirical management science for global leadership.',
    introHeading: 'Introduction to Doctor of Philosophy in Business Administration',
    introParagraphs: [
      "The Doctor of Philosophy (PhD) in Business Administration at Akwa Ibom State University Business School is an intensive, research-driven terminal degree designed for top-tier academics, policy economists, and corporate chief research strategists. The programme is crafted to push the boundaries of management knowledge through rigorous scientific inquiry and innovative empirical methodologies.",
      "Unlike practitioner doctorates, the PhD focuses heavily on epistemological rigor, theoretical conceptualisation, advanced financial econometrics, and structural equation modelling. Candidates work closely with distinguished doctoral supervisors to produce high-impact contributions publishable in world-class peer-reviewed management journals.",
      "Graduates of this programme emerge as authoritative thought leaders capable of directing university faculties, leading international research institutes, steering macroeconomic policy think tanks, and advising Fortune 500 boards on organizational resilience."
    ],
    whyApply: [
      {
        title: "Frontier Empirical Research",
        desc: "Master advanced quantitative modeling (SEM, AMOS, SmartPLS) and qualitative phenomenology to produce groundbreaking management scholarship."
      },
      {
        title: "Doctoral Supervisory Board",
        desc: "Learn under senior professors and Fellows of the Academy of Management with extensive global research citation records."
      },
      {
        title: "Flexible Modular Colloquiums",
        desc: "Structured modular weekend colloquiums tailored for university lecturers, research fellows, and corporate chief economists."
      },
      {
        title: "Publication Mentorship",
        desc: "Dedicated guidance towards publishing in Scopus-indexed and Web of Science corporate governance journals prior to thesis defence."
      }
    ],
    requirements: [
      "A recognised Master's degree (M.Sc. or MBA with a research-based dissertation) in Business Administration or closely related discipline from an approved university.",
      "A minimum cumulative grade point average (CGPA) of 3.50 on a 5.00 point scale (or at least 60% overall average in postgraduate coursework).",
      "Submission and defense of a comprehensive 5-page Doctoral Research Concept Note outlining research problem, theoretical framework, and proposed methodology.",
      "Five (5) credit passes in WASSCE/NECO/GCE O'Level including English Language and Mathematics.",
      "Two academic letters of recommendation from professors familiar with candidate's analytical capability."
    ],
    curriculum: [
      {
        year: "Year 1 (Advanced Foundations & Methodologies)",
        courses: [
          { code: "BUS 901", title: "Advanced Epistemology & Scientific Inquiry", units: 3 },
          { code: "BUS 902", title: "Multivariate Statistical Analysis & Econometrics", units: 3 },
          { code: "BUS 903", title: "Seminars in Strategic Management & Enterprise Theory", units: 3 },
          { code: "BUS 904", title: "Advanced Qualitative Research & Grounded Theory", units: 3 }
        ]
      },
      {
        year: "Year 2 (Doctoral Colloquium & Proposal Defence)",
        courses: [
          { code: "BUS 905", title: "Doctoral Seminar on Corporate Governance & Ethics", units: 3 },
          { code: "BUS 906", title: "Comprehensive Doctoral Examination", units: 6 },
          { code: "BUS 990", title: "Dissertation Proposal Defence (Senate Panel)", units: 6 }
        ]
      },
      {
        year: "Year 3 - 5 (Empirical Fieldwork & Thesis Defence)",
        courses: [
          { code: "BUS 999", title: "Original Empirical Dissertation Fieldwork & Viva Voce", units: 33 }
        ]
      }
    ],
    careerOutcomes: [
      "University Professor, Dean, or Head of Business Department",
      "Director-General of National Economic & Policy Think Tanks",
      "Chief Strategy Officer / Corporate Head of Research",
      "Senior Research Fellow at International Development Agencies (UN, World Bank, AfDB)",
      "Lead Management Consultant for Multinationals"
    ]
  },

  'dba': {
    id: 'dba',
    code: 'DBA',
    degree: 'Doctor of Business Administration (DBA)',
    name: 'Doctor of Business Administration (DBA)',
    department: 'Business Administration',
    category: 'Practitioner Executive Track',
    badge: 'Flagship Executive Doctorate',
    duration: '3 Years (36 Months / 6 Semesters)',
    mode: 'Executive Hybrid (Alternate Weekends + Digital LMS)',
    feePerSession: '₦950,000',
    feePerSemester: '₦500,000',
    totalCredits: '54 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'The terminal executive doctoral credential designed for boardroom leaders and C-suite champions.',
    introHeading: 'Introduction to Doctor of Business Administration (DBA)',
    introParagraphs: [
      "The Doctor of Business Administration (DBA) at Akwa Ibom State University Business School is a professional practitioner doctorate of highest distinction. It is specifically engineered for business executives, captains of industry, permanent secretaries, managing directors, and senior corporate leaders who seek to generate applied solutions for complex organizational challenges.",
      "While a PhD generates abstract academic theory, the DBA bridges high-level scientific rigor with practical boardroom application. Candidates leverage advanced methodologies—such as Saunders' Research Onion and Methodological Triangulation—to address real-world bottlenecks in corporate governance, supply chain resilience, strategic agility, and digital innovation.",
      "The programme is delivered through flexible alternate weekend executive masterclasses at the Main Campus in Ikot Akpaden, supplemented by our executive digital learning management system (LMS) and interactive Silicon AI research assistant. This structure allows senior leaders to earn a prestigious doctorate without interrupting their corporate or public service responsibilities."
    ],
    whyApply: [
      {
        title: "Boardroom-Driven Research",
        desc: "Solve genuine corporate bottlenecks in your industry rather than hypothetical academic puzzles, delivering immediate ROI to your enterprise."
      },
      {
        title: "Peer C-Suite Cohort",
        desc: "Learn alongside Chief Executive Officers, Directors-General, Financial Controllers, and Founders across high-impact sectors."
      },
      {
        title: "Executive Smart LMS & Silicon AI",
        desc: "Access asynchronous masterclasses, Saunders' Research Onion interactive modules, and 24/7 methodological support via Silicon AI."
      },
      {
        title: "Doctor of Distinction Title",
        desc: "Graduate with the full prestige of an accredited doctorate from a premier Nigerian state university with full NUC approval."
      }
    ],
    requirements: [
      "A recognised Master's degree (MBA, M.Sc., or relevant executive master's) in any discipline from an accredited university.",
      "A minimum of five (5) years of verifiable managerial, executive, or entrepreneurial leadership experience in the corporate or public sector.",
      "Submission of a 3-page Executive Problem Statement (Concept Note) outlining an operational dilemma you intend to investigate.",
      "Five (5) O'Level credit passes including English Language and Mathematics at WASSCE/NECO/GCE.",
      "Successful performance at the Executive Admissions Screening Board interview."
    ],
    curriculum: [
      {
        year: "Year 1 (Term 1 - Term 3: Foundations & Analytics)",
        courses: [
          { code: "DBA 801", title: "Introduction to Research Philosophies and Paradigms", units: 3 },
          { code: "DBA 802", title: "Analytic Techniques & Managerial Decision Models", units: 6 },
          { code: "DBA 803", title: "Practicum in Business Administration (T3 to T5)", units: 6 }
        ]
      },
      {
        year: "Year 2 (Term 4 - Term 6: Executive Synthesis)",
        courses: [
          { code: "DBA 804", title: "Academic Writing and Seminar Presentation", units: 3 },
          { code: "DBA 805", title: "Advanced Corporate Governance & Strategic Policy", units: 3 },
          { code: "DBA 890", title: "Executive Capstone Colloquium & Proposal Defence", units: 6 }
        ]
      },
      {
        year: "Year 3 (Term 7 - Term 18: Applied Dissertation)",
        courses: [
          { code: "DBA 899", title: "Applied Industry Dissertation & Viva Voce Defence", units: 36 }
        ]
      }
    ],
    careerOutcomes: [
      "Board Chairman / Non-Executive Director",
      "Group Chief Executive Officer (CEO) / Managing Director",
      "Permanent Secretary / Head of Government Parastatal",
      "Senior Partner / Principal at Top-Tier Management Consulting Firms",
      "Executive Adjunct Professor at Business Schools"
    ]
  },

  'mba': {
    id: 'mba',
    code: 'MBA',
    degree: 'Master of Business Administration (MBA)',
    name: 'Master of Business Administration (MBA)',
    department: 'Business Administration',
    category: "Master's Executive Track",
    badge: 'Flagship Executive Master',
    duration: '18 - 24 Months (4 Semesters)',
    mode: 'Executive Hybrid (Alternate Weekends + Digital LMS)',
    feePerSession: '₦550,000',
    feePerSemester: '₦290,000',
    totalCredits: '48 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'Transform into a visionary 360-degree business leader capable of driving competitive corporate advantage.',
    introHeading: 'Introduction to Master of Business Administration (MBA)',
    introParagraphs: [
      "The Executive Master of Business Administration (MBA) at Akwa Ibom State University Business School is the benchmark management degree for forward-looking professionals aiming to ascend into executive leadership. The curriculum provides a panoramic mastery of finance, strategic marketing, organizational psychology, supply chain optimization, and digital transformation.",
      "Designed with input from captains of industry in Nigeria and global business models, our MBA combines rigorous Harvard Business Review case pedagogy with real-time enterprise simulations. Learners dissect balance sheets, build market entry strategies, and master ethical boardroom negotiation.",
      "With alternate weekend delivery at the Main Campus in Ikot Akpaden and access to our executive LMS, candidates develop cross-functional expertise while continuing their full-time corporate, public service, or entrepreneurial careers."
    ],
    whyApply: [
      {
        title: "360° Management Acumen",
        desc: "Bridge finance, marketing, operations, and human capital into a cohesive executive framework for high-level decision making."
      },
      {
        title: "Executive Networking",
        desc: "Build lifelong strategic alliances with senior professionals across oil & gas, banking, telecommunications, and government MDAs."
      },
      {
        title: "Real-World Consulting Project",
        desc: "Solve a live corporate challenge for a sponsoring enterprise instead of submitting a purely theoretical library thesis."
      },
      {
        title: "Direct Gateway to DBA",
        desc: "Top graduates qualify for accelerated advancement into the Doctor of Business Administration (DBA) programme."
      }
    ],
    requirements: [
      "A recognised Bachelor's degree (minimum Second Class Honours, Lower Division) in any discipline from an accredited university.",
      "Holders of Postgraduate Diploma (PGD) with credit pass from recognised universities are also fully eligible.",
      "A minimum of three (3) years of verifiable post-NYSC professional, corporate, or entrepreneurial experience.",
      "Five (5) credit passes in WASSCE/NECO/GCE O'Level including English Language and Mathematics.",
      "Satisfactory performance in the executive admissions screening assessment."
    ],
    curriculum: [
      {
        year: "Year 1 (Semester 1 & 2: Core Management Toolkit)",
        courses: [
          { code: "MBA 801", title: "Managerial Economics & Macro Policy", units: 3 },
          { code: "MBA 802", title: "Financial Reporting & Managerial Accounting", units: 3 },
          { code: "MBA 803", title: "Corporate Finance & Investment Analysis", units: 3 },
          { code: "MBA 804", title: "Marketing Strategy in Digital Economies", units: 3 },
          { code: "MBA 805", title: "Organizational Behavior & Human Capital", units: 3 },
          { code: "MBA 806", title: "Operations & Supply Chain Analytics", units: 3 }
        ]
      },
      {
        year: "Year 2 (Semester 3 & 4: Strategic Capstone & Electives)",
        courses: [
          { code: "MBA 807", title: "Business Strategy & Boardroom Leadership", units: 3 },
          { code: "MBA 808", title: "Corporate Entrepreneurship & Innovation", units: 3 },
          { code: "MBA 809", title: "Business Law, Ethics & Corporate Governance", units: 3 },
          { code: "MBA 890", title: "Executive Industry Capstone Research Project", units: 6 }
        ]
      }
    ],
    careerOutcomes: [
      "General Manager / Chief Operating Officer (COO)",
      "Commercial Director / Head of Business Development",
      "Corporate Strategy & Financial Controller",
      "Senior Management Consultant",
      "High-Growth Business Founder / Managing Director"
    ]
  },

  'pgd-ba': {
    id: 'pgd-ba',
    code: 'PGD BA',
    degree: 'Postgraduate Diploma (PGD)',
    name: 'Postgraduate Diploma in Business Administration',
    department: 'Business Administration',
    category: 'Postgraduate Bridging Track',
    badge: 'Direct MBA Pathway',
    duration: '12 Months (2 Semesters)',
    mode: 'Weekend Intensive & Foundational Modules',
    feePerSession: '₦320,000',
    feePerSemester: '₦170,000',
    totalCredits: '30 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'The authoritative postgraduate bridge transforming HND and degree holders for executive advancement.',
    introHeading: 'Introduction to Postgraduate Diploma in Business Administration (PGD)',
    introParagraphs: [
      "The Postgraduate Diploma in Business Administration (PGD) at Akwa Ibom State University Business School is an intensive, high-rigor academic bridge. It is specially crafted for holders of Higher National Diploma (HND) qualifications, graduates with third-class honours, or professionals who obtained first degrees in non-business disciplines (such as engineering, sciences, agriculture, or arts).",
      "The curriculum delivers a robust, accelerated re-tooling in corporate management fundamentals, managerial accounting, quantitative business methods, commercial law, and organizational strategy. It systematically aligns candidates with modern corporate paradigms.",
      "Graduating with a credit average in the PGD programme confers direct, unconditional qualification for admission into the AKSU Executive Master of Business Administration (MBA) or Executive Master of Public Administration (EMPA)."
    ],
    whyApply: [
      {
        title: "Unconditional MBA Entry",
        desc: "Successfully completing the PGD bridges HND or third-class qualifications directly into full Executive Master's eligibility."
      },
      {
        title: "Fast-Track 12-Month Schedule",
        desc: "Complete the entire programme across two intensive weekend semesters without sacrificing your active career."
      },
      {
        title: "Management Fundamentals Re-Tooling",
        desc: "Gain fluency in financial statements, economic indicators, personnel dynamics, and market positioning."
      },
      {
        title: "University Senate Credential",
        desc: "Earn an official postgraduate diploma from an accredited state university with official transcripts."
      }
    ],
    requirements: [
      "A Higher National Diploma (HND) with a minimum of Lower Credit from an accredited polytechnic or monotechnic in any discipline.",
      "OR a Third-Class Bachelor's degree from a recognised university in any discipline.",
      "OR a recognised first degree in a non-management discipline seeking management grounding.",
      "Five (5) credit passes at WASSCE/NECO/GCE O'Level including English Language and Mathematics."
    ],
    curriculum: [
      {
        year: "Semester 1 (Core Foundations)",
        courses: [
          { code: "PGD 701", title: "Principles of Modern Management", units: 3 },
          { code: "PGD 702", title: "Financial & Cost Accounting Fundamentals", units: 3 },
          { code: "PGD 703", title: "Quantitative Techniques for Business Decisions", units: 3 },
          { code: "PGD 704", title: "Managerial Economics & Market Systems", units: 3 }
        ]
      },
      {
        year: "Semester 2 (Applied Management & Capstone)",
        courses: [
          { code: "PGD 705", title: "Marketing Management & Customer Strategy", units: 3 },
          { code: "PGD 706", title: "Human Resource Management & Labour Relations", units: 3 },
          { code: "PGD 707", title: "Business Law, Contracts & Statutory Frameworks", units: 3 },
          { code: "PGD 790", title: "Postgraduate Project & Applied Case Study", units: 4 }
        ]
      }
    ],
    careerOutcomes: [
      "Mid-Level Administrative & Operational Manager",
      "Accounts & Administrative Officer in MDAs",
      "Small & Medium Enterprise (SME) Business Manager",
      "Immediate Candidate for Executive MBA Enrolment",
      "Corporate Project Officer"
    ]
  },

  'dpa': {
    id: 'dpa',
    code: 'DPA',
    degree: 'Doctor of Public Administration (DPA)',
    name: 'Doctor of Public Administration (DPA)',
    department: 'Public Administration',
    category: 'Public Sector Doctoral Track',
    badge: 'Terminal Governance Degree',
    duration: '3 Years (36 Months / 6 Semesters)',
    mode: 'Executive Weekend Policy Labs & Governance Capstone',
    feePerSession: '₦900,000',
    feePerSemester: '₦480,000',
    totalCredits: '54 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'The premier doctoral credential for permanent secretaries, legislators, and public policy leaders.',
    introHeading: 'Introduction to Doctor of Public Administration (DPA)',
    introParagraphs: [
      "The Doctor of Public Administration (DPA) at Akwa Ibom State University Business School is the terminal practitioner doctorate for senior civil servants, permanent secretaries, heads of extra-ministerial departments, commissioners, legislators, and international development advisers. It prepares leaders to formulate, analyze, and execute public policy that withstands political and socio-economic headwinds.",
      "The programme focuses on the contemporary realities of the public sector: public financial management under IPSAS standards, fiscal federalism, anti-corruption compliance, public procurement act governance, digital e-government architecture, and public-private partnerships (PPP).",
      "Candidates conduct applied policy research that directly benefits state governments, federal ministries, regional development commissions (such as NDDC), or multilateral agencies, cementing their position at the pinnacle of public sector stewardship."
    ],
    whyApply: [
      {
        title: "Public Policy Masterclasses",
        desc: "Examine comparative administrative jurisprudence, civil service reform, and legislative policy implementation."
      },
      {
        title: "Civil Service Advancement",
        desc: "Position yourself for apex postings including Permanent Secretary, Head of Service, Director-General, or Special Adviser."
      },
      {
        title: "Peer Policy Think Tank",
        desc: "Debate and collaborate with seasoned administrators, commissioners, and development partners in a confidential, high-level environment."
      },
      {
        title: "Executive Weekend Schedule",
        desc: "Modular scheduling designed to accommodate demanding civil service duties and official government commitments."
      }
    ],
    requirements: [
      "A Master's degree in Public Administration (MPA, M.Sc.), Political Science, International Relations, Law, or closely related social science discipline from an accredited university.",
      "A minimum of five (5) years of senior leadership experience in the public service, civil service, military, paramilitary, or diplomatic mission.",
      "A 3 to 5-page Policy Research Concept Note identifying a critical public administration or governance challenge in Nigeria.",
      "Five (5) credit passes in WASSCE/NECO/GCE O'Level including English Language and Mathematics.",
      "Satisfactory admissions screening interview with the Public Administration Doctoral Board."
    ],
    curriculum: [
      {
        year: "Year 1 (Governance Systems & Policy Design)",
        courses: [
          { code: "PAD 801", title: "Comparative Public Administration & Civil Service Systems", units: 3 },
          { code: "PAD 802", title: "Advanced Policy Formulation, Analysis & Implementation", units: 3 },
          { code: "PAD 803", title: "Public Financial Management & IPSAS Accounting Standards", units: 3 },
          { code: "PAD 804", title: "Public Sector Research Paradigms & Governance Metrics", units: 3 }
        ]
      },
      {
        year: "Year 2 (Administrative Jurisprudence & Colloquium)",
        courses: [
          { code: "PAD 805", title: "Administrative Law, Ethics & Anti-Corruption Frameworks", units: 3 },
          { code: "PAD 806", title: "Inter-Governmental Fiscal Relations & Decentralisation", units: 3 },
          { code: "PAD 890", title: "Doctoral Policy Colloquium & Proposal Defence", units: 6 }
        ]
      },
      {
        year: "Year 3 (Applied Governance Capstone)",
        courses: [
          { code: "PAD 899", title: "Applied Public Policy Dissertation & Oral Defense", units: 30 }
        ]
      }
    ],
    careerOutcomes: [
      "Federal or State Permanent Secretary",
      "Head of Civil Service / Secretary to State Government",
      "Director-General / CEO of Government MDAs",
      "Special Adviser to Governor / President on Policy Formulation",
      "International Civil Servant (UN, ECOWAS, AU, Commonwealth)"
    ]
  },

  'phd-af': {
    id: 'phd-af',
    code: 'DAF / PhD AF',
    degree: 'Doctor of Accounting and Finance',
    name: 'Doctor of Accounting and Finance',
    department: 'Accounting and Finance',
    category: 'Financial Economics Doctoral Track',
    badge: 'Premier Financial Doctorate',
    duration: '3 - 5 Years (6 - 10 Semesters)',
    mode: 'Advanced Financial Econometrics & Forensic Seminars',
    feePerSession: '₦920,000',
    feePerSemester: '₦490,000',
    totalCredits: '54 Credit Units',
    campus: 'Main Campus in Ikot Akpaden, Mkpat Enin',
    tagline: 'Master empirical financial econometrics, forensic accounting, and capital market architecture.',
    introHeading: 'Introduction to Doctor of Accounting and Finance',
    introParagraphs: [
      "The Doctor of Accounting and Finance (DAF / PhD) at Akwa Ibom State University Business School is a premier doctoral programme designed to prepare financial scholars, Chief Financial Officers (CFOs), senior audit partners, and fiscal regulators for world-class leadership. It combines empirical capital market theory with rigorous forensic auditing and econometric modeling.",
      "Candidates explore frontier areas of international financial architecture, including IFRS compliance, international tax treaty optimization, fintech risk governance, corporate valuation under macroeconomic volatility, and forensic fraud detection.",
      "Under the guidance of renowned faculty—including Dr Uwem Etim Uwah, PhD, FCA, ACTI—scholars produce original empirical dissertations that inform Central Bank policies, capital market reforms, and institutional investor strategies."
    ],
    whyApply: [
      {
        title: "Financial Econometrics Mastery",
        desc: "Master time-series econometrics, panel data regression (Stata, EViews, R), and asset pricing models for empirical research."
      },
      {
        title: "Big 4 & ICAN Partnership Alignment",
        desc: "Curriculum designed in harmony with the Institute of Chartered Accountants of Nigeria (ICAN) and CITN benchmarks."
      },
      {
        title: "Forensic Investigation Focus",
        desc: "Advanced techniques in digital forensics, illicit financial flow tracking, and institutional fraud risk assessment."
      },
      {
        title: "Executive Weekend Structure",
        desc: "Optimized for CFOs, Senior Partners, and Central Bank executives to complete their doctorate while maintaining career momentum."
      }
    ],
    requirements: [
      "A recognised Master's degree (M.Sc., MBA, or M.Phil) in Accounting, Banking, Finance, or Economics from an accredited university.",
      "A minimum CGPA of 3.50 on a 5.00 point scale (or 60% average mark in postgraduate coursework).",
      "Professional accounting certification (FCA, ACA, ACTI, ANAN, CFA, or ACCA) is highly recommended and weighted favourably.",
      "A 5-page Research Concept Note detailing a proposed investigation in corporate finance, taxation, auditing, or capital markets.",
      "Five (5) O'Level credit passes including English Language, Mathematics, and Economics."
    ],
    curriculum: [
      {
        year: "Year 1 (Empirical Foundations & Financial Theory)",
        courses: [
          { code: "ACC 901", title: "Advanced Financial Accounting & IFRS Disclosure Theory", units: 3 },
          { code: "FIN 902", title: "Empirical Asset Pricing & Corporate Finance Theory", units: 3 },
          { code: "ACC 903", title: "Financial Econometrics & Time-Series Modeling", units: 3 },
          { code: "FIN 904", title: "International Taxation Strategy & Capital Flight", units: 3 }
        ]
      },
      {
        year: "Year 2 (Forensics & Doctoral Colloquium)",
        courses: [
          { code: "ACC 905", title: "Forensic Accounting & Corporate Fraud Analytics", units: 3 },
          { code: "FIN 906", title: "Banking Regulation & Monetary Policy Modeling", units: 3 },
          { code: "ACC 990", title: "Doctoral Proposal Defense (Senate Board)", units: 6 }
        ]
      },
      {
        year: "Year 3 - 5 (Applied Financial Dissertation)",
        courses: [
          { code: "ACC 999", title: "Empirical Dissertation in Accounting/Finance & Viva Voce", units: 30 }
        ]
      }
    ],
    careerOutcomes: [
      "Chief Financial Officer (CFO) / Executive Director of Finance",
      "Senior Partner in Global Audit & Assurance Firms (PwC, KPMG, EY, Deloitte)",
      "Director of Monetary Policy / Banking Supervision (Central Bank of Nigeria)",
      "Professor / Chair of Accounting and Finance",
      "Chairman of Corporate Audit & Risk Committees"
    ]
  }
};

export const ProgrammeDetailPage = ({ 
  programmeId = 'dba', 
  onSelectProgramme, 
  setActiveView 
}) => {
  const [activeTab, setActiveTab] = useState('overview'); // overview, curriculum, requirements, tuition, outcomes
  const [currentProgId, setCurrentProgId] = useState(programmeId);

  // Sync if prop changes
  React.useEffect(() => {
    if (programmeId) {
      setCurrentProgId(programmeId);
    }
  }, [programmeId]);

  const prog = PROGRAMME_DETAILS[currentProgId] || PROGRAMME_DETAILS['dba'];

  const handleProgrammeSwitch = (id) => {
    setCurrentProgId(id);
    if (onSelectProgramme) {
      onSelectProgramme(id);
    }
    setActiveTab('overview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900 pb-20 font-sans">
      
      {/* ======================================================== */}
      {/* 1. TOP BREADCRUMB & PROGRAMME FAST SELECTOR BAR          */}
      {/* ======================================================== */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-slate-500 font-sc">
              <button 
                onClick={() => setActiveView('home')}
                className="hover:text-[#0B2644] transition-colors cursor-pointer"
              >
                Home
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <button 
                onClick={() => {
                  setActiveView('home');
                  setTimeout(() => {
                    document.getElementById('programmes')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="hover:text-[#0B2644] transition-colors cursor-pointer"
              >
                Academic Programmes
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-bold text-[#7A470E]">{prog.code}</span>
            </div>

            {/* Return to Portal Button */}
            <button
              onClick={() => setActiveView('home')}
              className="inline-flex items-center text-xs font-bold text-[#0B2644] hover:text-[#7A470E] font-sc space-x-1 cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Portal Overview</span>
            </button>
          </div>

          {/* Quick Programme Selector Pills (The 6 Academic Degrees from Screenshot) */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center space-x-2 overflow-x-auto pb-1 text-xs font-sc font-bold">
            <span className="text-slate-400 font-medium shrink-0 mr-1 hidden sm:inline">Explore Degrees:</span>
            {Object.values(PROGRAMME_DETAILS).map((p) => (
              <button
                key={p.id}
                onClick={() => handleProgrammeSwitch(p.id)}
                className={`px-3 py-1.5 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center space-x-1.5 ${
                  currentProgId === p.id
                    ? 'bg-[#0B2644] text-white shadow-xs ring-2 ring-[#7A470E]/50 font-bold'
                    : 'bg-slate-100 text-slate-700 hover:bg-amber-50 hover:text-[#7A470E] border border-slate-200'
                }`}
              >
                <span>{p.code}</span>
              </button>
            ))}
          </div>

        </div>
      </div>

      {/* ======================================================== */}
      {/* 2. EXECUTIVE HERO STAGE: PROGRAMME SHOWCASE              */}
      {/* ======================================================== */}
      <section className="bg-gradient-to-br from-[#030F1C] via-[#06182B] to-[#0B2644] text-white py-14 sm:py-20 relative overflow-hidden border-b-4 border-[#7A470E]">
        {/* Ambient Decorative Glow Orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A470E]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* Left 8 Cols: Programme Identity & Context */}
            <div className="lg:col-span-8 space-y-6">
              
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-[#7A470E] text-white border border-[#B87A28] font-sc shadow-sm">
                  {prog.badge}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-slate-200 border border-white/20 font-sc">
                  Department of {prog.department}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex items-center space-x-1 font-sc">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>NUC Fully Accredited</span>
                </span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black font-sc text-white leading-tight">
                {prog.name}
              </h1>

              <p className="text-base sm:text-xl text-slate-300 leading-relaxed font-light max-w-3xl">
                {prog.tagline}
              </p>

              {/* Fast Facts Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                  <span className="text-[11px] text-slate-400 block font-mono">DURATION</span>
                  <strong className="text-sm text-white font-sc">{prog.duration}</strong>
                </div>

                <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                  <span className="text-[11px] text-slate-400 block font-mono">STUDY FORMAT</span>
                  <strong className="text-sm text-white font-sc">{prog.mode}</strong>
                </div>

                <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                  <span className="text-[11px] text-slate-400 block font-mono">SESSION TUITION</span>
                  <strong className="text-sm text-[#B87A28] font-mono font-bold">{prog.feePerSession}</strong>
                </div>

                <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                  <span className="text-[11px] text-slate-400 block font-mono">LOCATION</span>
                  <strong className="text-sm text-white font-sc">Ikot Akpaden Main Campus</strong>
                </div>
              </div>

              {/* Primary Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={() => setActiveView('apply')}
                  className="px-6 py-3.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-black text-sm transition-all shadow-xl hover:scale-[1.02] flex items-center space-x-2 font-sc cursor-pointer border border-[#B87A28]"
                >
                  <Award className="w-4 h-4 text-[#B87A28]" />
                  <span>Begin Application (₦25,000)</span>
                  <ArrowRight className="w-4 h-4" />
                </button>

                <a
                  href={`https://wa.me/2348033380401?text=Hello%20AKSU%20Business%20School,%20I%20am%20inquiring%20about%20the%20${encodeURIComponent(prog.name)}%20programme.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all shadow-md flex items-center space-x-2 font-sc"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Admissions WhatsApp Desk</span>
                </a>
              </div>

            </div>

            {/* Right 4 Cols: Executive Accreditation Card */}
            <div className="lg:col-span-4">
              <div className="translucent-card-navy rounded-3xl p-6 sm:p-7 border-2 border-[#7A470E]/50 space-y-5 text-white shadow-2xl relative">
                <div className="flex items-center space-x-3 pb-3 border-b border-white/15">
                  <img src="/Logo.png" alt="AKSU Crest" className="w-12 h-12 object-contain logo-glow" />
                  <div>
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#B87A28] font-sc block">
                      University Academic Catalogue
                    </span>
                    <span className="text-sm font-bold font-sc">
                      October 2026 Commencement
                    </span>
                  </div>
                </div>

                <div className="space-y-3 text-xs text-slate-300">
                  <div className="flex items-center justify-between pb-2 border-b border-white/10">
                    <span className="text-slate-400">Application Fee:</span>
                    <strong className="text-white font-mono">₦25,000 (Instant Verification)</strong>
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-white/10">
                    <span className="text-slate-400">Acceptance Fee:</span>
                    <strong className="text-white font-mono">₦50,000 (Upon Provisional Offer)</strong>
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-white/10">
                    <span className="text-slate-400">Total Program Credits:</span>
                    <strong className="text-white font-mono">{prog.totalCredits}</strong>
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-white/10">
                    <span className="text-slate-400">Faculty Director:</span>
                    <strong className="text-[#B87A28] font-sc">Dr Uwem Etim Uwah, PhD</strong>
                  </div>
                </div>

                <div className="p-3 rounded-2xl bg-white/5 border border-white/10 text-[11px] text-slate-300 space-y-1">
                  <span className="font-bold text-[#B87A28] block font-sc">Executive Hybrid Delivery:</span>
                  <p>In-person masterclasses at Main Campus Smart Amphitheatre with digital coursework on the TrustStack-adapted LMS.</p>
                </div>

                <button
                  onClick={() => alert(`Downloading Official ${prog.name} Curriculum & Information Brochure (PDF)...`)}
                  className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition-colors flex items-center justify-center space-x-1.5 font-sc cursor-pointer border border-white/20"
                >
                  <Download className="w-3.5 h-3.5 text-[#B87A28]" />
                  <span>Download Curriculum Guide (PDF)</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ======================================================== */}
      {/* 3. NAVIGATION TABS (Overview, Curriculum, Requirements)   */}
      {/* ======================================================== */}
      <div className="bg-white border-b-2 border-slate-200 sticky top-14 z-10 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 sm:space-x-6 overflow-x-auto text-xs sm:text-sm font-sc font-bold py-1">
            {[
              { id: 'overview', label: 'Programme Overview', icon: BookOpen },
              { id: 'curriculum', label: 'Curriculum & Modules', icon: Layers },
              { id: 'requirements', label: 'Admission Requirements', icon: CheckCircle2 },
              { id: 'tuition', label: 'Tuition & Payment Structure', icon: DollarSign },
              { id: 'outcomes', label: 'Career & Executive Outcomes', icon: Compass }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3.5 px-3 border-b-4 -mb-[2px] transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${
                    isActive
                      ? 'border-[#7A470E] text-[#0B2644] font-black'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-[#7A470E]' : 'text-slate-400'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 4. MAIN PROGRAMME CONTENT DETAILS                        */}
      {/* ======================================================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* Main 8 Columns: Tab Content */}
          <div className="lg:col-span-8 space-y-8">
            
            {/* ================= TAB 1: OVERVIEW ================= */}
            {activeTab === 'overview' && (
              <div className="space-y-8 animate-in fade-in duration-200">
                
                {/* Introduction Narrative */}
                <div className="translucent-card-light rounded-3xl p-6 sm:p-8 space-y-4">
                  <div className="inline-flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-[#0B2644] bg-amber-50 px-3 py-1 rounded-lg border border-[#7A470E]/30 font-sc">
                    <Sparkles className="w-3.5 h-3.5 text-[#7A470E]" />
                    <span>Academic Context & Philosophy</span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644]">
                    {prog.introHeading}
                  </h2>

                  <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
                    {prog.introParagraphs.map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>
                </div>

                {/* Why You Should Apply (Miva Modelled Grid) */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-[#7A470E]" />
                    <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                      Why You Should Apply to {prog.code}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {prog.whyApply.map((item, idx) => (
                      <div 
                        key={idx}
                        className="translucent-card-light p-5 rounded-2xl space-y-2 border border-slate-200 hover:border-[#7A470E] transition-all group"
                      >
                        <div className="w-8 h-8 rounded-xl bg-amber-50 border border-[#7A470E]/30 flex items-center justify-center font-bold text-xs text-[#7A470E] font-mono group-hover:bg-[#0B2644] group-hover:text-white transition-colors">
                          0{idx + 1}
                        </div>
                        <h4 className="text-base font-black font-sc text-slate-900 group-hover:text-[#0B2644]">
                          {item.title}
                        </h4>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Director's Endorsement Box */}
                <div className="translucent-card-gold p-6 sm:p-7 rounded-3xl border-2 border-[#7A470E]/40 space-y-3">
                  <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                    Directorate Assurance
                  </span>
                  <blockquote className="text-sm sm:text-base italic text-slate-800 leading-relaxed">
                    "At Akwa Ibom State University Business School, we reject passive academic detachment. Whether enrolled in our PhD or executive DBA, our scholars are equipped to interrogate structural challenges in corporate Nigeria and generate empirical remedies that transform industry landscapes."
                  </blockquote>
                  <div className="pt-2 text-xs">
                    <strong className="text-[#0B2644] font-sc block font-bold text-sm">
                      Dr Uwem Etim Uwah, PhD, FCA, ACTI
                    </strong>
                    <span className="text-slate-600">Director, AKSU Business School • Senior University Scholar</span>
                  </div>
                </div>

              </div>
            )}

            {/* ================= TAB 2: CURRICULUM ================= */}
            {activeTab === 'curriculum' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="translucent-card-light rounded-3xl p-6 sm:p-8 space-y-3">
                  <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                    Curriculum Framework & Module Sequencing
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Designed in alignment with the National Universities Commission (NUC) Core Curriculum Minimum Academic Standards (CCMAS) and international postgraduate benchmarks.
                  </p>
                </div>

                <div className="space-y-6">
                  {prog.curriculum.map((level, idx) => (
                    <div key={idx} className="bg-white rounded-3xl border-2 border-slate-200 overflow-hidden shadow-xs">
                      <div className="bg-[#0B2644] text-white py-3.5 px-6 font-sc font-bold text-sm flex items-center justify-between">
                        <span>{level.year}</span>
                        <span className="text-xs text-[#B87A28] font-mono">Accredited Modules</span>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {level.courses.map((course, cIdx) => (
                          <div key={cIdx} className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center space-x-3">
                              <span className="px-2.5 py-1 rounded-md text-xs font-mono font-bold bg-amber-50 text-[#7A470E] border border-amber-200">
                                {course.code}
                              </span>
                              <div>
                                <h4 className="text-sm font-bold text-slate-900 font-sc">
                                  {course.title}
                                </h4>
                                <span className="text-[11px] text-slate-500">Core Compulsory Postgraduate Module</span>
                              </div>
                            </div>

                            <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-700">
                              {course.units} Credit Units
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ================= TAB 3: REQUIREMENTS ================= */}
            {activeTab === 'requirements' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="translucent-card-light rounded-3xl p-6 sm:p-8 space-y-4">
                  <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                    Entry Criteria & Eligibility Benchmark
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Candidates must satisfy the general postgraduate admissions regulations of the Akwa Ibom State University Postgraduate School.
                  </p>

                  <div className="space-y-3 pt-2">
                    {prog.requirements.map((req, rIdx) => (
                      <div key={rIdx} className="flex items-start space-x-3 p-3.5 rounded-2xl bg-white border border-slate-200 shadow-2xs">
                        <CheckCircle2 className="w-5 h-5 text-[#7A470E] shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">{req}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 rounded-3xl bg-blue-50/70 border border-blue-200 text-xs sm:text-sm text-slate-700 space-y-2">
                  <strong className="text-blue-950 font-sc block text-base font-bold">
                    Direct Admissions Assessment Guidance:
                  </strong>
                  <p>
                    Candidates with professional credentials (such as ICAN, NIM, CITN, CIPM, or international professional bodies) will have their professional portfolios credited during merit evaluation by the departmental screening committee.
                  </p>
                </div>
              </div>
            )}

            {/* ================= TAB 4: TUITION & FEES ================= */}
            {activeTab === 'tuition' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="translucent-card-light rounded-3xl p-6 sm:p-8 space-y-3">
                  <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                    Tuition Schedule & Investment Structure
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    AKSU Business School operates a transparent, all-inclusive executive fee structure with flexible installment arrangements.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-6 rounded-3xl bg-white border-2 border-[#7A470E] shadow-sm space-y-2">
                    <span className="text-xs font-bold text-slate-400 font-mono uppercase">Full Academic Session</span>
                    <div className="text-3xl font-black text-[#0B2644] font-mono">{prog.feePerSession}</div>
                    <span className="text-xs text-emerald-800 font-bold block">Annual Single Payment Option</span>
                    <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                      Covers full tuition, masterclass lecture materials, access to digital LMS & amphitheatre catering.
                    </p>
                  </div>

                  <div className="p-6 rounded-3xl bg-white border-2 border-slate-200 shadow-sm space-y-2">
                    <span className="text-xs font-bold text-slate-400 font-mono uppercase">Modular Installment Plan</span>
                    <div className="text-3xl font-black text-[#7A470E] font-mono">{prog.feePerSemester}</div>
                    <span className="text-xs text-slate-600 font-bold block">Per Semester Installment</span>
                    <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                      Paid prior to the commencement of each accredited academic semester.
                    </p>
                  </div>
                </div>

                <div className="p-5 rounded-3xl bg-amber-50/60 border border-amber-200 text-xs text-slate-700 space-y-2">
                  <strong className="text-[#7A470E] font-sc block text-sm">Official Fee Settlement Notice:</strong>
                  <p>
                    All statutory fees (Application Fee ₦25,000, Acceptance Fee ₦50,000, and Semester Tuition) are processed transparently via Paystack on the official executive portal, generating instant digital receipts and university matriculation tokens.
                  </p>
                </div>
              </div>
            )}

            {/* ================= TAB 5: OUTCOMES ================= */}
            {activeTab === 'outcomes' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <div className="translucent-card-light rounded-3xl p-6 sm:p-8 space-y-3">
                  <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                    Leadership Placements & Career Horizons
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Alumni of our postgraduate school lead impactful transformations across major corporations, government ministries, and multilateral organizations.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prog.careerOutcomes.map((career, cIdx) => (
                    <div key={cIdx} className="p-4 rounded-2xl bg-white border border-slate-200 flex items-center space-x-3 shadow-2xs">
                      <div className="w-8 h-8 rounded-xl bg-amber-100 text-[#7A470E] flex items-center justify-center font-bold text-xs shrink-0 font-sc">
                        ★
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-slate-800 font-sc">{career}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right 4 Columns: Sticky Quick Action Card & Switching Links */}
          <div className="lg:col-span-4 space-y-6 sticky top-28">
            
            {/* Quick Application Box */}
            <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm space-y-5">
              <div className="space-y-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                  Intake Application
                </span>
                <h4 className="text-lg font-black font-sc text-[#0B2644]">
                  Apply for {prog.code} Admission
                </h4>
                <p className="text-xs text-slate-500">
                  Applications for the October 2026 Commencement are actively processing.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Application Fee:</span>
                  <strong className="text-slate-900 font-mono">₦25,000</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Payment Gateway:</span>
                  <span className="font-bold text-emerald-700">Paystack Verified</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Lecture Commencement:</span>
                  <strong className="text-[#7A470E] font-mono">October 2026</strong>
                </div>
                <div className="pt-2 border-t border-slate-200/80 text-[10.5px] text-slate-500 leading-normal">
                  <em>Note: Application submission initiates credential audit and board screening; admission offers are competitive and subject to Senate approval.</em>
                </div>
              </div>

              <button
                onClick={() => setActiveView('apply')}
                className="w-full py-3.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black rounded-xl text-sm transition-all shadow-md flex items-center justify-center space-x-2 font-sc cursor-pointer"
              >
                <span>Submit Application Dossier (₦25,000)</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  setActiveView('admission-advert');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors flex items-center justify-center space-x-1.5 font-sc cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5 text-[#7A470E]" />
                <span>View Full Official Advert</span>
              </button>
            </div>

            {/* Quick Links to Other 5 Academic Degrees */}
            <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm space-y-4">
              <h4 className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc border-b border-slate-100 pb-2">
                All 6 Academic Degrees
              </h4>

              <div className="space-y-1.5 text-xs font-sc">
                {Object.values(PROGRAMME_DETAILS).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleProgrammeSwitch(p.id)}
                    className={`w-full text-left p-2.5 rounded-xl transition-all flex items-center justify-between cursor-pointer ${
                      currentProgId === p.id
                        ? 'bg-amber-50 text-[#7A470E] font-black border border-[#7A470E]/30'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-[#0B2644]'
                    }`}
                  >
                    <span>{p.name}</span>
                    <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                  </button>
                ))}
              </div>
            </div>

            {/* Inquiries & Campus Contacts */}
            <div className="p-5 rounded-3xl bg-[#06182B] text-white text-xs space-y-3">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-[#B87A28]" />
                <strong className="font-sc text-sm">Campus Admissions Desk</strong>
              </div>
              <p className="text-slate-300 text-xs">
                Main Campus in Ikot Akpaden, Mkpat Enin, Akwa Ibom State.
              </p>
              <div className="pt-1 space-y-1 text-slate-300 font-mono text-[11px]">
                <div>WhatsApp: +234 803 338 0401</div>
                <div>Email: aisubs@aksu.edu.ng</div>
              </div>
              <div className="pt-3 border-t border-slate-700/80">
                <span className="text-[10px] text-slate-400 font-sc block mb-1.5 uppercase tracking-wider">
                  Connect on Social Media:
                </span>
                <SocialPillButtons className="flex flex-col space-y-1.5" />
              </div>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
};
